import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { OrderStatus } from "@/api/lib/db/types";
import Icon from "../../lib/components/custom/Icon";
import { Image } from "../../lib/components/custom/Image";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../lib/components/ui/alert-dialog";
import { Badge } from "../../lib/components/ui/badge";
import { Button } from "../../lib/components/ui/button";
import { DialogContent } from "../../lib/components/ui/dialog";
import { useApi } from "../../lib/hooks/api/use-api";
import { cn } from "../../lib/utils";
import PrintableOrder from "./PrintableOrder";

interface OrderDetailsDialogProps {
	orderId: number;
	onStatusUpdate: (orderId: number, status: OrderStatus) => void;
	confirmingOrder: number | null;
}

export default function OrderDetailsDialog({
	orderId,
	onStatusUpdate,
	confirmingOrder,
}: OrderDetailsDialogProps) {
	const { orders } = useApi();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);
	const { t } = useTranslation();

	// Fetch order statuses
	const { data: orderStatusesData } = orders.getStatuses();

	// Fetch full order details including items
	const { data: orderDetails, isLoading } = orders.getById({
		orderId: orderId.toString(),
	});

	const fullOrder = orderDetails?.order;

	const getNextStatus = (currentStatus: OrderStatus): OrderStatus | null => {
		const transitions = orderStatusesData?.transitions;
		if (!transitions) return null;
		const nextStatuses = transitions[currentStatus as keyof typeof transitions];
		return nextStatuses?.[0] || null;
	};

	const getStatusColor = (status: OrderStatus, paymentStatus?: string) => {
		// Special case for cancelled orders due to failed payment
		if (status === "cancelled" && paymentStatus === "failed") {
			return "bg-red-600 text-white hover:bg-red-700 ring-2 ring-red-200";
		}
		// Pending = Stripe has not confirmed payment (danger)
		if (status === "pending") {
			return "bg-red-600 text-white hover:bg-red-700 ring-2 ring-red-200";
		}

		switch (status) {
			case "received":
				return "bg-destructive text-destructive-foreground hover:bg-destructive/90";
			case "preparing":
				return "bg-orange-500 text-white hover:bg-orange-600";
			case "out-for-delivery":
				return "bg-purple-500 text-white hover:bg-purple-600";
			case "delivered":
				return "bg-gray-500 text-white hover:bg-gray-600";
			case "cancelled":
				return "bg-red-500 text-white hover:bg-red-600";
			default:
				return "bg-gray-500 text-white hover:bg-gray-600";
		}
	};

	const getStatusText = (status: OrderStatus, paymentStatus?: string) => {
		// Special case for cancelled orders due to failed payment
		if (status === "cancelled" && paymentStatus === "failed") {
			return t("admin.order_details.payment_failed");
		}
		if (status === "pending") return t("admin.dashboard.status.pending_unpaid");

		switch (status) {
			case "received":
				return t("admin.dashboard.status.new_order");
			case "preparing":
				return t("admin.dashboard.status.preparing");
			case "out-for-delivery":
				return t("admin.dashboard.status.out_for_delivery");
			case "delivered":
				return t("admin.dashboard.status.delivered");
			case "cancelled":
				return t("admin.dashboard.status.cancelled");
			default:
				return status;
		}
	};

	const handleStatusClick = (status: OrderStatus) => {
		setPendingStatus(status);
		setConfirmDialogOpen(true);
	};

	const handleConfirmStatusUpdate = () => {
		if (pendingStatus && fullOrder) {
			onStatusUpdate(fullOrder.id, pendingStatus);
			setConfirmDialogOpen(false);
			setPendingStatus(null);
		}
	};

	if (isLoading || !fullOrder) {
		return (
			<DialogContent className="max-w-[95vw] sm:max-w-2xl p-0 overflow-hidden border-0 bg-card rounded-2xl h-[80vh]">
				<div className="h-full flex flex-col items-center justify-center space-y-4">
					<Icon
						name="RefreshCw"
						className="w-8 h-8 animate-spin text-primary"
					/>
					<p className="text-muted-foreground">
						{t("admin.order_details.loading")}
					</p>
				</div>
			</DialogContent>
		);
	}

	return (
		<>
			<DialogContent className="max-w-[95vw] sm:max-w-2xl p-0 max-h-[90dvh] overflow-hidden rounded-2xl border-0 bg-card flex flex-col">
				{/* Hero Header */}
				<div className="relative bg-linear-to-br from-primary/10 to-accent p-6 sm:p-8 flex flex-col gap-4">
					<div className="flex justify-between items-start z-10">
						<div>
							<div className="flex items-center gap-3 mb-2">
								<Badge
									className={cn(
										"px-3 py-1 text-sm shadow-sm",
										getStatusColor(
											fullOrder.status,
											fullOrder.paymentStatus || undefined,
										),
									)}
								>
									{getStatusText(
										fullOrder.status,
										fullOrder.paymentStatus || undefined,
									)}
								</Badge>
								<span className="text-xs text-muted-foreground bg-white/50 px-2 py-1 rounded-full backdrop-blur-sm">
									{new Date(fullOrder.createdAt).toLocaleString()}
								</span>
							</div>
							<h2 className="text-3xl font-bold tracking-tight text-foreground">
								Order #{fullOrder.id}
							</h2>
							<div className="flex items-center gap-2 mt-2 text-muted-foreground">
								<Icon name="User" className="w-4 h-4" />
								<span className="font-medium text-foreground">
									{fullOrder.user?.name ||
										t("admin.order_details.unknown_customer")}
								</span>
								{fullOrder.user?.email && (
									<span className="text-xs">({fullOrder.user.email})</span>
								)}
							</div>
						</div>
					</div>

					{/* Decorative Pattern overlay */}
					<div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,var(--tw-gradient-stops))] from-primary to-transparent" />
				</div>

				{/* Content Scroll Area */}
				<div className="flex-1 overflow-y-auto bg-gray-50/50">
					<div className="p-6 space-y-6">
						{/* Delivery Details Card */}
						<div className="bg-white rounded-xl p-5 border shadow-sm">
							<div className="flex items-center gap-2 mb-4">
								<div className="p-2 bg-primary/10 rounded-lg text-primary">
									<Icon name="MapPin" className="w-5 h-5" />
								</div>
								<h3 className="font-semibold text-lg">
									{t("admin.order_details.delivery_details")}
								</h3>
							</div>

							<div className="grid gap-4 sm:grid-cols-2">
								<div className="space-y-1">
									<p className="text-sm font-medium text-muted-foreground">
										{t("admin.order_details.address")}
									</p>
									<p className="text-base">
										{fullOrder.address?.streetAddress}
									</p>
									<p className="text-sm text-muted-foreground">
										{fullOrder.address?.city}, {fullOrder.address?.state}{" "}
										{fullOrder.address?.zipCode}
									</p>
								</div>
								<div className="space-y-1">
									<p className="text-sm font-medium text-muted-foreground">
										{t("admin.order_details.contact")}
									</p>
									{fullOrder.address?.phone ? (
										<p className="flex items-center gap-2">
											<Icon
												name="Phone"
												className="w-3 h-3 text-muted-foreground"
											/>
											{fullOrder.address.phone}
										</p>
									) : (
										<p className="text-sm text-muted-foreground italic">
											{t("admin.order_details.no_phone")}
										</p>
									)}
								</div>
							</div>

							{fullOrder.address?.instructions && (
								<div className="mt-4 pt-4 border-t border-dashed">
									<p className="text-sm font-medium text-amber-600 mb-1 flex items-center gap-1">
										<Icon name="Info" className="w-3 h-3" />{" "}
										{t("admin.order_details.delivery_instructions")}
									</p>
									<p className="text-sm text-foreground bg-amber-50 p-3 rounded-lg border border-amber-100">
										{fullOrder.address.instructions}
									</p>
								</div>
							)}
						</div>

						{/* Order Items */}
						<div className="space-y-3">
							<h3 className="font-semibold text-lg px-1">
								{t("admin.order_details.order_items", {
									count: fullOrder.items?.length || 0,
								})}
							</h3>
							<div className="grid gap-3">
								{fullOrder.items && fullOrder.items.length > 0 ? (
									fullOrder.items.map((item) => (
										<div
											key={item.id}
											className="bg-white rounded-xl p-3 border shadow-sm flex gap-4 transition-all hover:shadow-md"
										>
											{/* Item Image */}
											<div className="shrink-0 w-20 h-20 bg-gray-100 rounded-lg overflow-hidden border">
												{item.menuItem?.imageUrl ? (
													<Image
														src={item.menuItem.imageUrl}
														alt={item.menuItem.name}
														className="w-full h-full object-cover"
													/>
												) : (
													<div className="w-full h-full flex items-center justify-center">
														<Icon
															name="Utensils"
															className="w-6 h-6 text-gray-400"
														/>
													</div>
												)}
											</div>

											{/* Item Details */}
											<div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
												<div>
													<div className="flex justify-between items-start gap-2">
														<div className="flex-1 min-w-0">
															<h4 className="font-semibold text-base truncate">
																{item.menuItem?.name}
																<span className="text-muted-foreground p-2 rounded-large bg-muted ml-2">
																	# {item.menuItem.displayId}
																</span>
															</h4>
														</div>
														<span className="font-bold text-primary">
															€{Number(item.totalPrice).toFixed(2)}
														</span>
													</div>
													<p className="text-sm text-muted-foreground flex items-center gap-2">
														<Badge
															variant="outline"
															className="text-xs px-1.5 py-0 h-5 font-normal"
														>
															{item.menuSize?.sizeName}
														</Badge>
														<span>× {item.quantity}</span>
													</p>
												</div>

												{/* Extras */}
												{item.extras && item.extras.length > 0 && (
													<div className="flex flex-wrap gap-1 mt-2">
														{item.extras?.map((extra, idx: number) => (
															<span
																key={`${extra.id}-${idx}`}
																className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200"
															>
																+ {extra.name}
															</span>
														))}
													</div>
												)}
											</div>
										</div>
									))
								) : (
									<div className="text-center py-8 text-muted-foreground bg-white rounded-xl border border-dashed">
										{t("admin.order_details.no_items")}
									</div>
								)}
							</div>
						</div>

						{/* Payment Summary */}
						<div className="bg-white rounded-xl p-5 border shadow-sm space-y-3">
							{(() => {
								const total = Number(fullOrder.totalPrice);
								const deliveryFee = Number(fullOrder.deliveryFee ?? 0);
								const subtotal = total - deliveryFee;
								return (
									<>
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin.order_details.subtotal")}
											</span>
											<span>€{subtotal.toFixed(2)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-muted-foreground">
												{t("admin.order_details.delivery_fee")}
											</span>
											<span>
												{deliveryFee === 0
													? t("admin.order_details.free")
													: `€${deliveryFee.toFixed(2)}`}
											</span>
										</div>
									</>
								);
							})()}
							<div className="border-t pt-3 flex justify-between items-center">
								<span className="font-bold text-lg">
									{t("admin.order_details.total")}
								</span>
								<span className="font-bold text-2xl text-primary">
									€{Number(fullOrder.totalPrice).toFixed(2)}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Footer Actions */}
				<div className="p-4 bg-white border-t flex flex-col sm:flex-row gap-3 items-center justify-between z-10">
					<div className="flex gap-2 w-full sm:w-auto">
						<Button
							variant="outline"
							onClick={() => handleStatusClick("cancelled")}
							className="w-full sm:w-auto text-destructive border-destructive/20 hover:bg-destructive/10"
						>
							{t("admin.order_details.cancel_order")}
						</Button>
						<Button
							variant="outline"
							onClick={() => window.print()}
							className="w-full sm:w-auto gap-2"
						>
							<Icon name="Printer" className="w-4 h-4" />
							{t("admin.order_details.print")}
						</Button>
					</div>

					<div className="flex gap-2 w-full sm:w-auto">
						{orderStatusesData &&
							(() => {
								const nextStatus = getNextStatus(fullOrder.status);
								return (
									nextStatus && (
										<Button
											variant="primary"
											onClick={() => handleStatusClick(nextStatus)}
											disabled={confirmingOrder === fullOrder.id}
											className="flex-1 sm:flex-none w-full sm:w-auto shadow-lg shadow-primary/20 overflow-hidden relative"
											size="lg"
											animatedContent={{
												key: nextStatus,
												children: (
													<>
														<span>
															{t("admin.order_details.mark_as", {
																status: getStatusText(nextStatus),
															})}
														</span>
														<Icon name="ArrowRight" className="w-4 h-4 ml-2" />
													</>
												),
											}}
										/>
									)
								);
							})()}
					</div>
				</div>
			</DialogContent>

			{/* Hidden Printable Receipt */}
			<div className="hidden print:block print-container">
				<PrintableOrder order={fullOrder} />
			</div>

			<AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("admin.order_details.confirm_title")}
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2">
								<p>
									{t("admin.order_details.confirm_description", {
										id: fullOrder.id,
										status: pendingStatus
											? getStatusText(pendingStatus)
											: "...",
									})}
								</p>
								{fullOrder.status === "pending" &&
									pendingStatus &&
									pendingStatus !== "cancelled" && (
										<p className="text-destructive font-medium">
											{t("admin.order_details.payment_warning")}
										</p>
									)}
								{pendingStatus === "cancelled" &&
									fullOrder.paymentStatus === "succeeded" && (
										<p className="text-amber-700 font-medium">
											{t("admin.order_details.cancel_refund_warning")}
										</p>
									)}
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("admin.order_details.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmStatusUpdate}
							className="bg-primary text-primary-foreground hover:bg-primary/90"
						>
							{t("admin.order_details.confirm_update")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
